import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import LoadingSpinner from './LoadingSpinner';

/**
 * Site Health Monitoring Tab with AI-Powered Insights
 * 
 * Shows:
 * - Overall system health status with AI explanations
 * - API endpoint health checks
 * - Cache health and coverage analysis
 * - Historical comparison (vs yesterday)
 * - AI-generated actionable recommendations
 */
const SiteHealthTab = () => {
  const [healthData, setHealthData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    endpoints: true,
    queryEngine: true,
    cache: true,
    insights: true
  });
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Fetch all health data
  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const checks = {
        cacheStats: { status: 'unknown', latency: 0, error: null, data: null },
        nlOdds: { status: 'unknown', latency: 0, error: null },
        playersCache: { status: 'unknown', latency: 0, error: null },
      };
      
      // Test Cache Stats endpoint
      try {
        const start1 = Date.now();
        const res1 = await fetch(`${functionsUrl}/getCacheStats`);
        checks.cacheStats.latency = Date.now() - start1;
        if (res1.ok) {
          const data = await res1.json();
          checks.cacheStats.status = data.success ? 'healthy' : 'error';
          checks.cacheStats.data = data;
        } else {
          checks.cacheStats.status = 'error';
          checks.cacheStats.error = `HTTP ${res1.status}`;
        }
      } catch (err) {
        checks.cacheStats.status = 'error';
        checks.cacheStats.error = err.message;
      }
      
      // Test NL Odds endpoint (with simple query) and capture engine health
      try {
        const start2 = Date.now();
        const res2 = await fetch(`${functionsUrl}/nlOdds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: 'nba moneyline' })
        });
        checks.nlOdds.latency = Date.now() - start2;
        if (res2.ok) {
          const nlData = await res2.json();
          checks.nlOdds.status = 'healthy';
          checks.nlOdds.engineHealth = nlData._engineHealth || null;
        } else {
          checks.nlOdds.status = 'error';
          checks.nlOdds.error = `HTTP ${res2.status}`;
        }
      } catch (err) {
        checks.nlOdds.status = 'error';
        checks.nlOdds.error = err.message;
      }
      
      // Test Players Cache endpoint
      try {
        const start3 = Date.now();
        const res3 = await fetch(`${functionsUrl}/getPlayersCacheStats`);
        checks.playersCache.latency = Date.now() - start3;
        checks.playersCache.status = res3.ok ? 'healthy' : 'error';
        if (!res3.ok) checks.playersCache.error = `HTTP ${res3.status}`;
      } catch (err) {
        checks.playersCache.status = 'error';
        checks.playersCache.error = err.message;
      }
      
      // Calculate overall health
      const statusValues = Object.values(checks).map(c => c.status);
      const allHealthy = statusValues.every(s => s === 'healthy');
      const anyError = statusValues.some(s => s === 'error');
      
      const healthResult = {
        overallStatus: allHealthy ? 'healthy' : (anyError ? 'degraded' : 'unknown'),
        lastCheck: new Date(),
        checks,
        cacheData: checks.cacheStats.data
      };
      
      setHealthData(healthResult);
      
      // Fetch AI insights after health data is loaded
      fetchAiInsights(healthResult);
      
    } catch (err) {
      console.error('[SiteHealth] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Fetch AI-generated insights
  const fetchAiInsights = async (healthResult) => {
    try {
      setAiLoading(true);
      
      const res = await fetch(`${functionsUrl}/getHealthInsights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          healthData: healthResult,
          cacheStats: healthResult.cacheData
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setAiInsights(data.insights);
        }
      }
    } catch (err) {
      console.error('[SiteHealth] AI Insights error:', err);
      // Non-critical, don't show error to user
    } finally {
      setAiLoading(false);
    }
  };
  
  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);
  
  const onRefresh = () => {
    setRefreshing(true);
    fetchHealth();
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return '#10B981';
      case 'degraded': return '#F59E0B';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      default: return '#6B7280';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return 'checkmark-circle';
      case 'degraded': return 'warning';
      case 'error': return 'close-circle';
      default: return 'help-circle';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'healthy': return 'All Systems Operational';
      case 'degraded': return 'Degraded Performance';
      case 'error': return 'System Issues Detected';
      default: return 'Unknown Status';
    }
  };
  
  const getCacheHealthExplanation = (health) => {
    switch (health) {
      case 'healthy':
        return 'Cache was refreshed within the last 70 minutes. Users are seeing fresh odds data.';
      case 'stale':
        return 'Cache is 1-2 hours old. Data is still usable but may not reflect latest odds movements.';
      case 'critical':
        return 'Cache is over 2 hours old. Users may see outdated information. Consider triggering a manual refresh.';
      case 'never_refreshed':
        return 'Cache has never been refreshed. No data is available for users.';
      default:
        return 'Unable to determine cache status.';
    }
  };
  
  // ⭐⭐⭐ UPDATED THRESHOLDS per user requirements:
  // Query performance: <30s = good, 30-45s = okay, 45-60s = needs work, >60s = bad
  // API health checks use faster thresholds (these are just pings, not full queries)
  const getLatencyExplanation = (latency, isQueryPerformance = false) => {
    if (isQueryPerformance) {
      // For actual query performance (21s average etc)
      if (latency < 30000) return { status: 'excellent', text: 'Good response time' };
      if (latency < 45000) return { status: 'good', text: 'Acceptable response time' };
      if (latency < 60000) return { status: 'slow', text: 'Needs optimization' };
      return { status: 'critical', text: 'Slow - needs attention' };
    }
    // For API health check pings (faster thresholds for simple pings)
    if (latency < 3000) return { status: 'excellent', text: 'Excellent response time' };
    if (latency < 10000) return { status: 'good', text: 'Good response time' };
    if (latency < 25000) return { status: 'slow', text: 'Slower than normal' };
    return { status: 'critical', text: 'Very slow - may affect user experience' };
  };

  // Render loading state
  if (loading && !healthData) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <LoadingSpinner size={32} color="#8B5CF6" />
          <Text style={styles.loadingText}>Running health checks...</Text>
        </View>
      </View>
    );
  }
  
  // Render error state
  if (error && !healthData) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Ionicons name="alert-circle" size={48} color="#EF4444" />
          <Text style={styles.errorTitle}>Health Check Failed</Text>
          <Text style={styles.errorMessage}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchHealth}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  const cacheData = healthData?.cacheData;
  
  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#8B5CF6" />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>System Health</Text>
          <View style={styles.lastCheckBadge}>
            <Ionicons name="time-outline" size={14} color="#94A3B8" />
            <Text style={styles.lastCheckText}>
              {healthData?.lastCheck?.toLocaleTimeString()}
            </Text>
          </View>
        </View>
        <Text style={styles.subtitle}>Real-time monitoring with AI-powered insights</Text>
      </View>
      
      {/* AI Insights Panel - Top Priority */}
      {(aiInsights || aiLoading) && (
        <TouchableOpacity 
          style={styles.aiCard}
          onPress={() => toggleSection('insights')}
          activeOpacity={0.8}
        >
          <View style={styles.aiHeader}>
            <View style={styles.aiHeaderLeft}>
              <Ionicons name="sparkles" size={20} color="#8B5CF6" />
              <Text style={styles.aiTitle}>AI Health Analysis</Text>
            </View>
            <Ionicons 
              name={expandedSections.insights ? 'chevron-up' : 'chevron-down'} 
              size={20} 
              color="#94A3B8" 
            />
          </View>
          
          {expandedSections.insights && (
            <View style={styles.aiBody}>
              {aiLoading ? (
                <View style={styles.aiLoadingContainer}>
                  <ActivityIndicator size="small" color="#8B5CF6" />
                  <Text style={styles.aiLoadingText}>Analyzing system health...</Text>
                </View>
              ) : aiInsights ? (
                <>
                  {/* Executive Summary */}
                  <View style={styles.aiSection}>
                    <Text style={styles.aiSummary}>{aiInsights.summary}</Text>
                  </View>
                  
                  {/* Key Metrics Explained */}
                  {aiInsights.explanations && aiInsights.explanations.length > 0 && (
                    <View style={styles.aiSection}>
                      <Text style={styles.aiSectionTitle}>What This Means</Text>
                      {aiInsights.explanations.map((exp, idx) => (
                        <View key={idx} style={styles.explanationItem}>
                          <View style={[styles.explanationDot, { backgroundColor: getStatusColor(exp.status) }]} />
                          <View style={styles.explanationContent}>
                            <Text style={styles.explanationLabel}>{exp.metric}</Text>
                            <Text style={styles.explanationText}>{exp.explanation}</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                  
                  {/* Recommendations */}
                  {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                    <View style={styles.aiSection}>
                      <Text style={styles.aiSectionTitle}>Recommendations</Text>
                      {aiInsights.recommendations.map((rec, idx) => (
                        <View key={idx} style={styles.recommendationItem}>
                          <Ionicons 
                            name={rec.priority === 'high' ? 'alert-circle' : rec.priority === 'medium' ? 'information-circle' : 'bulb'} 
                            size={16} 
                            color={rec.priority === 'high' ? '#EF4444' : rec.priority === 'medium' ? '#F59E0B' : '#10B981'} 
                          />
                          <Text style={styles.recommendationText}>{rec.text}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                  
                  {/* Historical Comparison - Daily/Weekly/Monthly */}
                  {(aiInsights.comparison || aiInsights.historicalComparison) && (
                    <View style={styles.aiSection}>
                      <Text style={styles.aiSectionTitle}>Trend Analysis</Text>
                      {aiInsights.comparison && (
                        <Text style={styles.comparisonText}>{aiInsights.comparison}</Text>
                      )}
                      {aiInsights.historicalComparison?.weekly && (
                        <View style={styles.historicalRow}>
                          <Text style={styles.historicalLabel}>Weekly ({aiInsights.historicalComparison.weekly.daysTracked || 0}d):</Text>
                          <Text style={styles.historicalValue}>
                            {aiInsights.historicalComparison.weekly.totalQueries || 0} queries total
                          </Text>
                        </View>
                      )}
                      {aiInsights.historicalComparison?.monthly && (
                        <View style={styles.historicalRow}>
                          <Text style={styles.historicalLabel}>Monthly ({aiInsights.historicalComparison.monthly.daysTracked || 0}d):</Text>
                          <Text style={styles.historicalValue}>
                            {aiInsights.historicalComparison.monthly.totalQueries || 0} queries total
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                </>
              ) : (
                <Text style={styles.aiPlaceholder}>
                  AI insights will appear here once analysis is complete.
                </Text>
              )}
            </View>
          )}
        </TouchableOpacity>
      )}
      
      {/* Overall Status Card */}
      <TouchableOpacity 
        style={styles.card}
        onPress={() => toggleSection('overview')}
        activeOpacity={0.8}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Overall Status</Text>
          <Ionicons 
            name={expandedSections.overview ? 'chevron-up' : 'chevron-down'} 
            size={20} 
            color="#94A3B8" 
          />
        </View>
        
        {expandedSections.overview && (
          <>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(healthData?.overallStatus) + '20' }]}>
              <Ionicons 
                name={getStatusIcon(healthData?.overallStatus)} 
                size={24} 
                color={getStatusColor(healthData?.overallStatus)} 
              />
              <Text style={[styles.statusText, { color: getStatusColor(healthData?.overallStatus) }]}>
                {getStatusText(healthData?.overallStatus)}
              </Text>
            </View>
            
            <View style={styles.explanationBox}>
              <Ionicons name="information-circle-outline" size={16} color="#8B5CF6" />
              <Text style={styles.explanationBoxText}>
                {healthData?.overallStatus === 'healthy' 
                  ? 'All critical systems are functioning normally. API endpoints are responsive and cache data is fresh.'
                  : healthData?.overallStatus === 'degraded'
                  ? 'Some systems are experiencing issues. Check individual service statuses below for details.'
                  : 'Multiple systems are reporting errors. Immediate attention may be required.'}
              </Text>
            </View>
          </>
        )}
      </TouchableOpacity>
      
      {/* Service Endpoints Card */}
      <TouchableOpacity 
        style={styles.card}
        onPress={() => toggleSection('endpoints')}
        activeOpacity={0.8}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Service Endpoints</Text>
          <Ionicons 
            name={expandedSections.endpoints ? 'chevron-up' : 'chevron-down'} 
            size={20} 
            color="#94A3B8" 
          />
        </View>
        
        {expandedSections.endpoints && (
          <View style={styles.servicesContainer}>
            {/* Cache Stats API */}
            <View style={styles.serviceItem}>
              <View style={styles.serviceHeader}>
                <View style={styles.serviceLeft}>
                  <View style={[styles.statusDot, { backgroundColor: getStatusColor(healthData?.checks?.cacheStats?.status) }]} />
                  <Text style={styles.serviceName}>Cache Stats API</Text>
                </View>
                <Text style={[
                  styles.serviceLatency,
                  { color: getLatencyExplanation(healthData?.checks?.cacheStats?.latency || 0).status === 'excellent' ? '#10B981' :
                           getLatencyExplanation(healthData?.checks?.cacheStats?.latency || 0).status === 'good' ? '#94A3B8' :
                           getLatencyExplanation(healthData?.checks?.cacheStats?.latency || 0).status === 'slow' ? '#F59E0B' : '#EF4444' }
                ]}>
                  {healthData?.checks?.cacheStats?.latency}ms
                </Text>
              </View>
              <Text style={styles.serviceExplanation}>
                {healthData?.checks?.cacheStats?.status === 'healthy' 
                  ? `Healthy - ${getLatencyExplanation(healthData?.checks?.cacheStats?.latency || 0).text}. This endpoint provides cache statistics for the admin dashboard.`
                  : `Error: ${healthData?.checks?.cacheStats?.error || 'Unknown'}. The cache monitoring system is not responding correctly.`}
              </Text>
              {healthData?.checks?.cacheStats?.error && (
                <View style={styles.errorBadge}>
                  <Ionicons name="alert-circle" size={12} color="#EF4444" />
                  <Text style={styles.errorBadgeText}>{healthData.checks.cacheStats.error}</Text>
                </View>
              )}
            </View>
            
            {/* NL Odds API */}
            <View style={styles.serviceItem}>
              <View style={styles.serviceHeader}>
                <View style={styles.serviceLeft}>
                  <View style={[styles.statusDot, { backgroundColor: getStatusColor(healthData?.checks?.nlOdds?.status) }]} />
                  <Text style={styles.serviceName}>Natural Language Query API</Text>
                </View>
                <Text style={[
                  styles.serviceLatency,
                  { color: getLatencyExplanation(healthData?.checks?.nlOdds?.latency || 0).status === 'excellent' ? '#10B981' :
                           getLatencyExplanation(healthData?.checks?.nlOdds?.latency || 0).status === 'good' ? '#94A3B8' :
                           getLatencyExplanation(healthData?.checks?.nlOdds?.latency || 0).status === 'slow' ? '#F59E0B' : '#EF4444' }
                ]}>
                  {healthData?.checks?.nlOdds?.latency}ms
                </Text>
              </View>
              <Text style={styles.serviceExplanation}>
                {healthData?.checks?.nlOdds?.status === 'healthy' 
                  ? `Healthy - ${getLatencyExplanation(healthData?.checks?.nlOdds?.latency || 0).text}. This is the main search API that powers user queries.`
                  : `Error: ${healthData?.checks?.nlOdds?.error || 'Unknown'}. Users may be unable to search for odds.`}
              </Text>
              {healthData?.checks?.nlOdds?.error && (
                <View style={styles.errorBadge}>
                  <Ionicons name="alert-circle" size={12} color="#EF4444" />
                  <Text style={styles.errorBadgeText}>{healthData.checks.nlOdds.error}</Text>
                </View>
              )}
            </View>
            
            {/* Players Cache API */}
            <View style={styles.serviceItem}>
              <View style={styles.serviceHeader}>
                <View style={styles.serviceLeft}>
                  <View style={[styles.statusDot, { backgroundColor: getStatusColor(healthData?.checks?.playersCache?.status) }]} />
                  <Text style={styles.serviceName}>Players Cache API</Text>
                </View>
                <Text style={[
                  styles.serviceLatency,
                  { color: getLatencyExplanation(healthData?.checks?.playersCache?.latency || 0).status === 'excellent' ? '#10B981' :
                           getLatencyExplanation(healthData?.checks?.playersCache?.latency || 0).status === 'good' ? '#94A3B8' :
                           getLatencyExplanation(healthData?.checks?.playersCache?.latency || 0).status === 'slow' ? '#F59E0B' : '#EF4444' }
                ]}>
                  {healthData?.checks?.playersCache?.latency}ms
                </Text>
              </View>
              <Text style={styles.serviceExplanation}>
                {healthData?.checks?.playersCache?.status === 'healthy' 
                  ? `Healthy - ${getLatencyExplanation(healthData?.checks?.playersCache?.latency || 0).text}. Player data is available for autocomplete and search.`
                  : `Error: ${healthData?.checks?.playersCache?.error || 'Unknown'}. Player search autocomplete may not work.`}
              </Text>
              {healthData?.checks?.playersCache?.error && (
                <View style={styles.errorBadge}>
                  <Ionicons name="alert-circle" size={12} color="#EF4444" />
                  <Text style={styles.errorBadgeText}>{healthData.checks.playersCache.error}</Text>
                </View>
              )}
            </View>
          </View>
        )}
      </TouchableOpacity>

      {/* Query Engine Health Card */}
      <TouchableOpacity
        style={styles.card}
        onPress={() => toggleSection('queryEngine')}
        activeOpacity={0.8}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Query Engine</Text>
          <Ionicons
            name={expandedSections.queryEngine ? 'chevron-up' : 'chevron-down'}
            size={20}
            color="#94A3B8"
          />
        </View>

        {expandedSections.queryEngine && (() => {
          const eh = healthData?.checks?.nlOdds?.engineHealth;
          if (!eh) {
            return (
              <View style={styles.metricRow}>
                <Text style={styles.metricLabel}>Status</Text>
                <Text style={[styles.metricValue, { color: '#94A3B8' }]}>No data yet</Text>
              </View>
            );
          }
          const errorPct = parseFloat(eh.errorRate) || 0;
          const avgScore = parseFloat(eh.avgScore) || 0;
          const statusColor = errorPct > 10 ? '#EF4444' : errorPct > 2 ? '#F59E0B' : '#10B981';
          const scoreColor = avgScore >= 0.7 ? '#10B981' : avgScore >= 0.4 ? '#F59E0B' : '#EF4444';
          return (
            <View>
              <View style={styles.metricRow}>
                <Text style={styles.metricLabel}>Total Queries</Text>
                <Text style={styles.metricValue}>{eh.totalQueries}</Text>
              </View>
              <View style={styles.metricRow}>
                <Text style={styles.metricLabel}>Error Rate</Text>
                <Text style={[styles.metricValue, { color: statusColor }]}>{eh.errorRate}</Text>
              </View>
              <View style={styles.metricRow}>
                <Text style={styles.metricLabel}>Avg Confidence</Text>
                <Text style={[styles.metricValue, { color: scoreColor }]}>{eh.avgScore}</Text>
              </View>
              <View style={styles.metricRow}>
                <Text style={styles.metricLabel}>Cold Starts</Text>
                <Text style={styles.metricValue}>{eh.coldStarts}</Text>
              </View>
              {eh.lastError && (
                <View style={styles.metricRow}>
                  <Text style={styles.metricLabel}>Last Error</Text>
                  <Text style={[styles.metricValue, { color: '#EF4444', fontSize: 11 }]} numberOfLines={1}>{eh.lastError}</Text>
                </View>
              )}
              <View style={[styles.explanationBox, { marginTop: 8, backgroundColor: statusColor + '15' }]}>
                <Text style={[styles.explanationBoxText, { color: statusColor }]}>
                  {errorPct === 0 ? 'Engine running clean — no errors detected.'
                    : errorPct <= 2 ? 'Minimal errors — engine is healthy.'
                    : errorPct <= 10 ? 'Elevated error rate — investigate recent queries.'
                    : 'High error rate — engine needs immediate attention.'}
                </Text>
              </View>
            </View>
          );
        })()}
      </TouchableOpacity>
      
      {/* Cache Health Card */}
      <TouchableOpacity 
        style={styles.card}
        onPress={() => toggleSection('cache')}
        activeOpacity={0.8}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Cache Health</Text>
          <Ionicons 
            name={expandedSections.cache ? 'chevron-up' : 'chevron-down'} 
            size={20} 
            color="#94A3B8" 
          />
        </View>
        
        {expandedSections.cache && cacheData && (
          <>
            {/* Cache Status Badge */}
            <View style={[styles.cacheStatusBadge, { backgroundColor: getStatusColor(cacheData.cache_health) + '15' }]}>
              <Ionicons 
                name={getStatusIcon(cacheData.cache_health === 'healthy' ? 'healthy' : cacheData.cache_health === 'stale' ? 'degraded' : 'error')} 
                size={20} 
                color={getStatusColor(cacheData.cache_health === 'healthy' ? 'healthy' : cacheData.cache_health === 'stale' ? 'degraded' : 'error')} 
              />
              <Text style={[styles.cacheStatusText, { color: getStatusColor(cacheData.cache_health === 'healthy' ? 'healthy' : cacheData.cache_health === 'stale' ? 'degraded' : 'error') }]}>
                {cacheData.cache_health?.charAt(0).toUpperCase() + cacheData.cache_health?.slice(1)} Cache
              </Text>
            </View>
            
            <View style={styles.explanationBox}>
              <Ionicons name="information-circle-outline" size={16} color="#8B5CF6" />
              <Text style={styles.explanationBoxText}>
                {getCacheHealthExplanation(cacheData.cache_health)}
              </Text>
            </View>
            
            {/* Cache Metrics */}
            <View style={styles.metricsGrid}>
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{cacheData.current_state?.total_fixtures || 0}</Text>
                <Text style={styles.metricLabel}>Total Fixtures</Text>
                <Text style={styles.metricExplanation}>Games in cache</Text>
              </View>
              
              <View style={styles.metricItem}>
                <Text style={[styles.metricValue, { color: '#10B981' }]}>{cacheData.current_state?.fixtures_with_odds || 0}</Text>
                <Text style={styles.metricLabel}>With Odds</Text>
                <Text style={styles.metricExplanation}>Have betting data</Text>
              </View>
              
              <View style={styles.metricItem}>
                <Text style={[styles.metricValue, { color: '#F59E0B' }]}>{cacheData.current_state?.fixtures_without_odds || 0}</Text>
                <Text style={styles.metricLabel}>Upcoming</Text>
                <Text style={styles.metricExplanation}>Odds not posted yet</Text>
              </View>
              
              <View style={styles.metricItem}>
                <Text style={styles.metricValue}>{cacheData.current_state?.total_markets?.toLocaleString() || 0}</Text>
                <Text style={styles.metricLabel}>Markets</Text>
                <Text style={styles.metricExplanation}>Betting options</Text>
              </View>
            </View>
            
            {/* Last Refresh Info */}
            <View style={styles.refreshInfo}>
              <View style={styles.refreshRow}>
                <Text style={styles.refreshLabel}>Last Refresh:</Text>
                <Text style={styles.refreshValue}>
                  {cacheData.last_refresh?.age_minutes != null 
                    ? `${cacheData.last_refresh.age_minutes} minutes ago` 
                    : 'Never'}
                </Text>
              </View>
              <View style={styles.refreshRow}>
                <Text style={styles.refreshLabel}>Duration:</Text>
                <Text style={styles.refreshValue}>
                  {cacheData.last_refresh?.duration_seconds 
                    ? `${cacheData.last_refresh.duration_seconds.toFixed(2)}s` 
                    : 'N/A'}
                </Text>
              </View>
              <View style={styles.refreshRow}>
                <Text style={styles.refreshLabel}>Next Auto-Refresh:</Text>
                <Text style={styles.refreshValue}>{cacheData.next_refresh || 'Unknown'}</Text>
              </View>
            </View>
            
            {/* League Breakdown */}
            {cacheData.current_state?.by_league && Object.keys(cacheData.current_state.by_league).length > 0 && (
              <View style={styles.leagueSection}>
                <Text style={styles.leagueSectionTitle}>Coverage by League</Text>
                <View style={styles.leagueGrid}>
                  {Object.entries(cacheData.current_state.by_league)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 12)
                    .map(([league, count]) => (
                      <View key={league} style={styles.leagueTag}>
                        <Text style={styles.leagueTagText}>{league.toUpperCase()}</Text>
                        <Text style={styles.leagueTagCount}>{count}</Text>
                      </View>
                    ))}
                </View>
              </View>
            )}
          </>
        )}
      </TouchableOpacity>
      
      {/* Quick Actions */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Quick Actions</Text>
        <View style={styles.actionsRow}>
          <TouchableOpacity style={styles.actionButton} onPress={fetchHealth}>
            <Ionicons name="refresh" size={18} color="#FFFFFF" />
            <Text style={styles.actionButtonText}>Refresh Health Check</Text>
          </TouchableOpacity>
        </View>
      </View>
      
      {/* System Info */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>System Information</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Environment:</Text>
          <View style={[styles.envBadge, { backgroundColor: __DEV__ ? '#F59E0B20' : '#10B98120' }]}>
            <Text style={[styles.envText, { color: __DEV__ ? '#F59E0B' : '#10B981' }]}>
              {__DEV__ ? 'Development' : 'Production'}
            </Text>
          </View>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Functions URL:</Text>
          <Text style={styles.infoValueSmall} numberOfLines={1}>{functionsUrl}</Text>
        </View>
      </View>
      
      {/* Auto-refresh indicator */}
      <View style={styles.footer}>
        <Ionicons name="sync-outline" size={14} color="#64748B" />
        <Text style={styles.footerText}>Auto-refreshes every 60 seconds</Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    color: '#94A3B8',
    fontSize: 16,
    marginTop: 16,
  },
  errorTitle: {
    color: '#EF4444',
    fontSize: 20,
    fontWeight: 'bold',
    marginTop: 16,
    marginBottom: 8,
  },
  errorMessage: {
    color: '#94A3B8',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    padding: 20,
    paddingBottom: 12,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
  },
  subtitle: {
    color: '#94A3B8',
    fontSize: 14,
  },
  lastCheckBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#1E293B',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  lastCheckText: {
    color: '#94A3B8',
    fontSize: 12,
  },
  
  // AI Card Styles
  aiCard: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#8B5CF620',
    overflow: 'hidden',
  },
  aiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#8B5CF610',
  },
  aiHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  aiTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  aiBody: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#334155',
  },
  aiLoadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    paddingVertical: 20,
  },
  aiLoadingText: {
    color: '#94A3B8',
    fontSize: 14,
  },
  aiSection: {
    marginBottom: 16,
  },
  aiSummary: {
    color: '#F1F5F9',
    fontSize: 15,
    lineHeight: 24,
    fontStyle: 'italic',
  },
  aiSectionTitle: {
    color: '#8B5CF6',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  explanationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  explanationDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 6,
    marginRight: 10,
  },
  explanationContent: {
    flex: 1,
  },
  explanationLabel: {
    color: '#F1F5F9',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  explanationText: {
    color: '#94A3B8',
    fontSize: 13,
    lineHeight: 20,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 10,
    paddingLeft: 4,
  },
  recommendationText: {
    color: '#CBD5E1',
    fontSize: 13,
    lineHeight: 20,
    flex: 1,
  },
  comparisonText: {
    color: '#94A3B8',
    fontSize: 13,
    lineHeight: 20,
    marginBottom: 8,
  },
  historicalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderTopWidth: 1,
    borderTopColor: 'rgba(148, 163, 184, 0.1)',
  },
  historicalLabel: {
    color: '#64748B',
    fontSize: 12,
  },
  historicalValue: {
    color: '#CBD5E1',
    fontSize: 12,
    fontWeight: '500',
  },
  aiPlaceholder: {
    color: '#64748B',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },
  
  // Card Styles
  card: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 10,
    marginBottom: 12,
  },
  statusText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  explanationBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    backgroundColor: '#8B5CF610',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#8B5CF6',
  },
  explanationBoxText: {
    color: '#CBD5E1',
    fontSize: 13,
    lineHeight: 20,
    flex: 1,
  },
  
  // Service Styles
  servicesContainer: {
    gap: 16,
  },
  serviceItem: {
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
    paddingBottom: 16,
  },
  serviceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  serviceLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  serviceName: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '500',
  },
  serviceLatency: {
    fontSize: 14,
    fontWeight: '600',
  },
  serviceExplanation: {
    color: '#94A3B8',
    fontSize: 13,
    lineHeight: 20,
    marginLeft: 20,
  },
  errorBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#EF444420',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    marginTop: 8,
    marginLeft: 20,
    alignSelf: 'flex-start',
  },
  errorBadgeText: {
    color: '#EF4444',
    fontSize: 12,
    fontWeight: '500',
  },
  
  // Cache Styles
  cacheStatusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  cacheStatusText: {
    fontSize: 15,
    fontWeight: '600',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 16,
  },
  metricItem: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#0F172A',
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  metricValue: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
  },
  metricLabel: {
    color: '#94A3B8',
    fontSize: 13,
    fontWeight: '500',
    marginTop: 4,
  },
  metricExplanation: {
    color: '#64748B',
    fontSize: 11,
    marginTop: 2,
  },
  refreshInfo: {
    marginTop: 16,
    backgroundColor: '#0F172A',
    padding: 14,
    borderRadius: 10,
  },
  refreshRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  refreshLabel: {
    color: '#94A3B8',
    fontSize: 13,
  },
  refreshValue: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
  leagueSection: {
    marginTop: 16,
  },
  leagueSectionTitle: {
    color: '#94A3B8',
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 10,
  },
  leagueGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  leagueTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#374151',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
  },
  leagueTagText: {
    color: '#D1D5DB',
    fontSize: 11,
    fontWeight: '600',
  },
  leagueTagCount: {
    color: '#8B5CF6',
    fontSize: 11,
    fontWeight: '700',
  },
  
  // Actions Styles
  actionsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#8B5CF6',
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 8,
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  
  // Info Styles
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  infoLabel: {
    color: '#94A3B8',
    fontSize: 14,
  },
  infoValueSmall: {
    color: '#FFFFFF',
    fontSize: 11,
    maxWidth: '60%',
  },
  envBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  envText: {
    fontSize: 12,
    fontWeight: '600',
  },
  
  // Footer
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: 20,
  },
  footerText: {
    color: '#64748B',
    fontSize: 12,
    fontStyle: 'italic',
  },
});

export default SiteHealthTab;
